import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../client';
import {
  acceptAssignmentContract,
  rejectAssignmentContract,
  resolveUnclearAssignmentContract,
  mergeAssignmentContract,
  type AcceptAssignmentResponse,
  type RejectAssignmentRequest,
  type ResolveUnclearAssignmentRequest,
  type MergeAssignmentRequest,
  type MergeAssignmentResponse
} from '@bliks-io/contracts';
import { mergeCandidateKeys, recordingDashboardKeys, taskKeys } from '../query-keys';
import {
  dropRevokedProcessRead,
  invalidateProcessStatus,
  refreshProcessDetail
} from '../process-read-cache';

/**
 * Accept assignment mutation
 *
 * Accepts a process step assignment and creates a new recording.
 * - Invalidates tasks cache on success
 * - Refreshes the process detail: the assignment placeholder becomes a recording
 * - Returns the newly created process step recording ID
 */
export function useAcceptAssignment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      assignmentId
    }: {
      assignmentId: string;
      processId: string;
    }): Promise<AcceptAssignmentResponse> => {
      const path = acceptAssignmentContract.path.replace(':id', assignmentId);
      const { data } = await apiClient.post<AcceptAssignmentResponse>(path, undefined);
      return data;
    },
    onSuccess: async (_data, { processId }) => {
      queryClient.invalidateQueries({ queryKey: recordingDashboardKeys.all });
      refreshProcessDetail(queryClient, processId);
      // Awaited: the accept surface navigates away on success, and an
      // unawaited refetch is cancelled when my-tasks unmounts.
      await queryClient.invalidateQueries({ queryKey: taskKeys.all });
    }
  });
}

/**
 * Reject assignment mutation
 *
 * Rejects a process step assignment with a reason and optional reassignment.
 * - Invalidates tasks cache on success
 * - Can mark as unclear or reassign to another user
 * - Drops the process detail: the rejected assignment no longer grants read
 */
export function useRejectAssignment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      assignmentId,
      data
    }: {
      assignmentId: string;
      processId: string;
      data: RejectAssignmentRequest;
    }): Promise<void> => {
      const path = rejectAssignmentContract.path.replace(':id', assignmentId);
      await apiClient.post(path, data);
    },
    onSuccess: async (_data, { processId }) => {
      queryClient.invalidateQueries({ queryKey: taskKeys.all });
      queryClient.invalidateQueries({ queryKey: recordingDashboardKeys.all });
      await dropRevokedProcessRead(queryClient, processId);
    }
  });
}

/**
 * Resolve unclear assignment mutation
 *
 * Resolves a PENDING_UNCLEAR assignment by reassigning it to another user.
 * Creates a new chained assignment and marks the unclear one as resolved.
 * - Invalidates tasks cache on success
 * - Only works for PENDING_UNCLEAR status assignments
 * - Drops the process detail: the resolver hands the assignment on, so the
 *   involvement that granted the read ends with it (a resolver who reads the
 *   process through another grant simply refetches).
 */
export function useResolveUnclearAssignment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      assignmentId,
      data
    }: {
      assignmentId: string;
      processId: string;
      data: ResolveUnclearAssignmentRequest;
    }): Promise<void> => {
      const path = resolveUnclearAssignmentContract.path.replace(':id', assignmentId);
      await apiClient.post(path, data);
    },
    onSuccess: async (_data, { processId }) => {
      queryClient.invalidateQueries({ queryKey: taskKeys.all });
      queryClient.invalidateQueries({ queryKey: recordingDashboardKeys.all });
      await dropRevokedProcessRead(queryClient, processId);
    }
  });
}

/**
 * Merge assignment mutation
 *
 * Merges a pending assignment into an existing completed step recording.
 * - Invalidates tasks and merge-candidates caches on success
 * - Refreshes the process detail: the merge target gains an input edge and the
 *   assignment placeholder disappears (read survives via that recording)
 */
export function useMergeAssignment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      assignmentId,
      data
    }: {
      assignmentId: string;
      processId: string;
      data: MergeAssignmentRequest;
    }): Promise<MergeAssignmentResponse> => {
      const path = mergeAssignmentContract.path.replace(':assignmentId', assignmentId);
      const { data: response } = await apiClient.post<MergeAssignmentResponse>(path, data);
      return response;
    },
    onSuccess: (_data, { processId }) => {
      queryClient.invalidateQueries({ queryKey: taskKeys.all });
      queryClient.invalidateQueries({ queryKey: mergeCandidateKeys.all });
      // The merge runs the completion checker server-side.
      invalidateProcessStatus(queryClient);
      refreshProcessDetail(queryClient, processId);
    }
  });
}
